import { useContext, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  FiX,
  FiUploadCloud,
  FiRotateCcw,
  FiLoader,
  FiCheckCircle,
} from "react-icons/fi";
import { ScanOutlined } from "@ant-design/icons";

import { shareContacts } from "../../../../api/UserRequest";
import { updateTapCount } from "../../../helpers/tapCountMutation";
import { PlanValidationContext } from "../../../store/PlanValidationContext";
import Tesseract from "tesseract.js";
import { parseCardText } from "./parseCardText";
import preprocessImage from "./preprocessImage";
import { parsePhoneIntl } from "./dialCodeToCountry";

function ExchangeContact({ customButton, details, cardId }) {
  const queryClient = useQueryClient();
  const [planValidationData] = useContext(PlanValidationContext);

  const [open, setOpen] = useState(false);
  const [shared, setShared] = useState(false);
  const [loader, setLoader] = useState(false);

  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("ae");
  const [formValues, setFormValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // ---- card scanner state ----
  const [activeTab, setActiveTab] = useState("manual"); // 'manual' | 'scan'
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [scannedOnce, setScannedOnce] = useState(false);
  const fileInputRef = useRef(null);

  const handleTap = (button) => {
    updateTapCount(queryClient, button);
  };

  const onUploadButtonHandleClick = () => {
    handleTap("exchangeContact");
    setOpen(true);
  };

  if (!planValidationData.exchangeContact) return null;

  const resetAll = () => {
    setShared(false);
    setActiveTab("manual");
    setScanning(false);
    setScanError(null);
    setScannedOnce(false);
    setFormValues({});
    setFieldErrors({});
    setPhone("");
    setPhoneCountry("ae");
  };

  const handleCancelUploadModal = () => {
    setOpen(false);
    resetAll();
  };

  const updateField = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const capitalizeFirstLetter = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  const validate = () => {
    const errors = {};
    (details?.fields || []).forEach((f) => {
      if (f.name === "phone") {
        const digits = (phone || "").replace(/\D/g, "");
        if (f.required && !digits) {
          errors.phone = "Please input your phone!";
        } else if (digits && digits.length < 6) {
          errors.phone = "Phone number should be greater than 6 digits";
        }
      } else if (f.required && !formValues[f.name]) {
        errors[f.name] = `Please input your ${f.name}!`;
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onFinish = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const datas = new FormData();
    (details?.fields || []).forEach((f) => {
      if (f.name === "phone") {
        datas.append("phone", phone);
      } else {
        datas.append(f.name, formValues[f.name] || "");
      }
    });

    try {
      setLoader(true);
      const { data } = await shareContacts(cardId, datas);
      if (data.success) {
        setShared(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  /* ------------------ CARD SCANNER: OCR + PARSING ----------------- */

  const applyScannedFields = (parsed) => {
    const fieldNames = (details?.fields || []).map((f) => f.name);
    const patch = {};

    if (fieldNames.includes("name") && parsed.name) patch.name = parsed.name;
    if (fieldNames.includes("email") && parsed.email) patch.email = parsed.email;
    if (fieldNames.includes("design") && parsed.design) patch.design = parsed.design;

    setFormValues((prev) => ({ ...prev, ...patch }));

    if (fieldNames.includes("phone") && parsed.phone) {
      const digits = parsed.phone.replace(/\D/g, "");
      const { country } = parsePhoneIntl(parsed.phone);
      setPhone(digits);
      setPhoneCountry(country);
    }
  };

  const runOcr = async (fileOrUrl) => {
    setScanning(true);
    setScanError(null);
    try {
      const { data } = await Tesseract.recognize(fileOrUrl, "eng");
      const text = data?.text?.trim();

      if (!text || text.length < 4) {
        setScanError(
          "Couldn't read any text from that image. Try a clearer photo, or enter details manually."
        );
        setScanning(false);
        return;
      }

      const parsed = parseCardText(text);

      if (!parsed.name && !parsed.email && !parsed.phone) {
        setScanError(
          "Couldn't confidently extract card details. Please check and fill in manually below."
        );
      }

      applyScannedFields(parsed);
      setScannedOnce(true);
    } catch (err) {
      console.log(err);
      setScanError("Scan failed. Please try again or enter details manually.");
    } finally {
      setScanning(false);
    }
  };

  const onCardFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setScanning(true);
      setScanError(null);
      const preprocessedBlob = await preprocessImage(file);
      await runOcr(preprocessedBlob);
    } catch (err) {
      console.error("Preprocessing failed, falling back to raw image:", err);
      setScanning(true);
      setScanError(null);
      await runOcr(file);
    } finally {
      e.target.value = "";
    }
  };

  const rescan = () => {
    setScannedOnce(false);
    setScanError(null);
    setActiveTab("scan");
    setFormValues({});
    setPhone("");
    setPhoneCountry("ae");
    setFieldErrors({});
  };

  const renderFields = () =>
    (details?.fields || []).map((obj, index) => (
      <div key={index} className="mb-3">
        {obj.name === "phone" ? (
          <div>
            <PhoneInput
              key={phoneCountry}
              country={phoneCountry}
              enableSearch
              value={phone}
              onChange={(val) => {
                setPhone(val);
                if (fieldErrors.phone) {
                  setFieldErrors((prev) => ({ ...prev, phone: undefined }));
                }
              }}
              inputStyle={{
                width: "100%",
                height: "44px",
                background: "#F5F5F6",
                border: "1px solid #ECECEE",
                borderRadius: "10px",
                fontSize: "14px",
                paddingLeft: "48px",
              }}
              buttonStyle={{
                background: "#F5F5F6",
                border: "1px solid #ECECEE",
                borderRight: "none",
                borderRadius: "10px 0 0 10px",
              }}
              dropdownStyle={{ fontSize: "13px" }}
            />
          </div>
        ) : (
          <input
            className="w-full bg-[#F5F5F6] border border-[#ECECEE] rounded-[10px] py-3.5 px-3.5 text-sm text-[#111214] outline-none placeholder:text-gray-400 focus:border-[#C7C9CD]"
            placeholder={capitalizeFirstLetter(obj.name)}
            type={obj.name === "email" ? "email" : "text"}
            value={formValues[obj.name] || ""}
            onChange={(e) => updateField(obj.name, e.target.value)}
          />
        )}
        {fieldErrors[obj.name] && (
          <div className="text-[11px] text-[#E0403A] mt-[5px] pl-0.5">
            {fieldErrors[obj.name]}
          </div>
        )}
      </div>
    ));

  if (!open) {
    return (
      <span onClick={onUploadButtonHandleClick} className="cursor-pointer">
        {customButton}
      </span>
    );
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 p-6 px-3">
      <div className="w-full max-w-[380px] max-h-[90vh] overflow-y-auto bg-white rounded-2xl p-[26px] shadow-[0_20px_50px_-18px_rgba(0,0,0,0.35)] relative">
        <button
          className="absolute top-[18px] right-[18px] bg-transparent border-none text-gray-400 cursor-pointer p-0.5 leading-none hover:text-gray-600"
          onClick={handleCancelUploadModal}
          aria-label="Close"
        >
          <FiX size={18} />
        </button>

        {shared ? (
          <div className="text-center pt-2 px-1">
            <div className="w-16 h-16 rounded-full bg-[#ECFBF0] text-[#34C759] flex items-center justify-center mx-auto mb-[18px]">
              <FiCheckCircle size={32} />
            </div>
            <div className="text-[17px] font-bold text-[#111214] mb-1">
              {details?.successMessage || "Contact Shared"}
            </div>
            <div className="text-[13px] text-gray-500 mb-[22px]">
              Your contact has been shared successfully.
            </div>

            {details?.fields?.length !== 0 && (
              <button
                className="w-full bg-[#1D1D1F] text-white border-none rounded-xl py-3.5 text-[14.5px] font-semibold cursor-pointer mt-1.5 hover:bg-black disabled:bg-[#C9CACD] disabled:cursor-not-allowed"
                onClick={handleCancelUploadModal}
              >
                Go Back
              </button>
            )}

            {details?.advButton && (
              <Link
                to="https://zeeqr.com/create-card"
                target="_blank"
                className="block w-full text-center bg-white text-[#111214] border border-[#E2E3E6] rounded-xl py-3 text-[13.5px] font-semibold mt-2.5 cursor-pointer no-underline hover:border-[#111214]"
              >
                Try ZEEQR Card
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="text-base font-semibold text-[#111214] mb-1">
              {details?.tittle || "Share your Contact Details"}
            </div>
            <div className="text-sm text-gray-500 mb-[18px]">
              {details?.description || "fill the contact details"}
            </div>

            <div className="flex bg-[#F1F2F4] rounded-[10px] p-[3px] mb-[18px]">
              <button
                type="button"
                className={`flex-1 border-none bg-transparent py-2 px-1.5 text-[12.5px] font-medium rounded-lg cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-150 ${
                  activeTab === "manual"
                    ? "bg-white text-[#111214] shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("manual")}
              >
                Manual Entry
              </button>
              <button
                type="button"
                className={`flex-1 border-none bg-transparent py-2 px-1.5 text-[12.5px] font-medium rounded-lg cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-150 ${
                  activeTab === "scan"
                    ? "bg-white text-[#111214] shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("scan")}
              >
                <ScanOutlined size={13} />
                Scan Card
              </button>
            </div>

            {activeTab === "scan" && !scannedOnce && (
              <div>
                <div
                  className="border-[1.5px] border-dashed border-[#D8DADD] rounded-xl py-[26px] px-3.5 text-center cursor-pointer mb-1 transition-all duration-150 hover:border-[#111214] hover:bg-[#FAFAFA]"
                  onClick={() => !scanning && fileInputRef.current.click()}
                >
                  {scanning ? (
                    <div className="py-[30px] px-3.5 text-center flex flex-col items-center">
                      <FiLoader size={22} className="animate-spin" />
                      <div className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5 mt-3">
                        Extracting details…
                      </div>
                    </div>
                  ) : (
                    <>
                    <div className="w-full">

                      <FiUploadCloud size={24} className="text-gray-500 mb-2 mx-auto" />
                    </div>
                      <div className="text-[13.5px] font-medium text-[#111214] mb-0.5">
                        Scan or Upload Business Card
                      </div>
                      <div className="text-[11.5px] text-gray-400">
                        JPG or PNG, front side up
                      </div>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={onCardFileSelected}
                    style={{ display: "none" }}
                  />
                </div>
                {scanError && (
                  <div className="text-[11.5px] text-[#E0403A] text-center mt-2.5">
                    {scanError}
                  </div>
                )}
              </div>
            )}

            {(activeTab === "manual" || scannedOnce) && (
              <form onSubmit={onFinish}>
                {scannedOnce && (
                  <div className="flex items-center justify-between bg-[#F5F5F6] rounded-[10px] py-2 px-3 mb-3 text-[11.5px] text-gray-500">
                    Scanned from card — edit if needed
                    <button
                      type="button"
                      className="bg-transparent border-none cursor-pointer text-[#111214] font-semibold text-[11.5px] flex items-center gap-1"
                      onClick={rescan}
                    >
                      <FiRotateCcw size={11} />
                      Rescan
                    </button>
                  </div>
                )}

                {renderFields()}

                {details?.fields?.length !== 0 && (
                  <button
                    className="w-full bg-[#1D1D1F] text-white border-none rounded-xl py-3.5 text-[14.5px] font-medium cursor-pointer mt-1.5 hover:bg-black disabled:bg-[#C9CACD] disabled:cursor-not-allowed"
                    type="submit"
                    disabled={loader}
                  >
                    {loader
                      ? "Submitting..."
                      : details?.submitButton || "Exchange Contact"}
                  </button>
                )}

                <button
                  type="button"
                  className="block text-center mt-4 mx-auto text-[13px] text-[#111214] underline bg-transparent border-none cursor-pointer"
                  onClick={handleCancelUploadModal}
                >
                  Cancel
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ExchangeContact;
